import { ReactNode } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { CurrencyField } from '../common/fields/CurrencyField'
import { Select } from '../common/fields/Select'
import { TextField } from '../common/fields/TextField'

type Props<PrimaryDropdownOptionT, SecondaryDropdownOptionT> = {
    title?: string
    subtitle?: string
    thirdLine?: ReactNode
    tertiaryView?: ReactNode
    primaryButtonTitle?: string
    onClickPrimaryButton?: () => void
    primaryDropdownOptions?: PrimaryDropdownOptionT[]
    onSelectPrimaryDropdownOption?: (option: PrimaryDropdownOptionT) => void
    selectedPrimaryDropdownOptionID?: string | null
    primaryDropdownClassName?: string
    primaryDropdownAlignment?: 'start' | 'end'
    secondaryDropdownOptions?: SecondaryDropdownOptionT[]
    onSelectSecondaryDropdownOption?: (option: SecondaryDropdownOptionT) => void
    selectedSecondaryDropdownOptionID?: string | null
    amount?: number
    onAmountChange?: (amount: number) => void
    quantity?: number
    onQuantityChange?: (quantity: number) => void
    secondaryTitle?: string
    secondarySubtitle?: string
    className?: string
}

export const SelectedItem = <
    PrimaryDropdownOptionT extends { id: string; title: string },
    SecondaryDropdownOptionT extends { id: string; title: string },
>({
    title,
    subtitle,
    thirdLine,
    tertiaryView,
    primaryButtonTitle,
    onClickPrimaryButton,
    primaryDropdownOptions,
    onSelectPrimaryDropdownOption,
    selectedPrimaryDropdownOptionID,
    primaryDropdownClassName,
    primaryDropdownAlignment = 'start',
    secondaryDropdownOptions,
    onSelectSecondaryDropdownOption,
    selectedSecondaryDropdownOptionID,
    amount,
    onAmountChange,
    quantity,
    onQuantityChange,
    secondaryTitle,
    secondarySubtitle,
    className,
}: Props<PrimaryDropdownOptionT, SecondaryDropdownOptionT>) => {
    return (
        <div
            className={twm(
                'group relative flex items-center justify-start gap-x-4 overflow-x-hidden rounded-lg border border-gray-300 bg-gray-100 px-4 py-3',
                onClickPrimaryButton !== undefined && 'cursor-pointer',
                className
            )}
            onClick={onClickPrimaryButton}
        >
            {onClickPrimaryButton !== undefined && (
                <div className="pointer-events-none absolute inset-0 z-10 group-hover:bg-black/4" />
            )}

            {tertiaryView !== undefined && <div className="shrink-0">{tertiaryView}</div>}

            {(title !== undefined || subtitle !== undefined || thirdLine !== undefined) && (
                <div className="flex min-w-0 shrink grow flex-col gap-y-1">
                    {title !== undefined && <Label size="sm">{title}</Label>}
                    {subtitle !== undefined && (
                        <Paragraph size="sm" className="truncate font-normal text-gray-600">
                            {subtitle}
                        </Paragraph>
                    )}
                    {thirdLine !== undefined && (
                        <Label size="sm" className="truncate font-light text-gray-600">
                            {thirdLine}
                        </Label>
                    )}
                </div>
            )}

            {primaryDropdownOptions !== undefined && (
                <div
                    className={twm(
                        'flex grow items-center',
                        primaryDropdownAlignment === 'start' ? 'justify-start' : 'justify-end'
                    )}
                >
                    <Select
                        options={primaryDropdownOptions}
                        onSelectOption={onSelectPrimaryDropdownOption}
                        selectedOptionID={selectedPrimaryDropdownOptionID}
                        className={twm('relative z-20 max-w-[220px]', primaryDropdownClassName)}
                        size="small"
                        maxDropdownHeight={256}
                    />
                </div>
            )}

            {secondaryDropdownOptions !== undefined && (
                <div onClick={(event) => event.stopPropagation()}>
                    <Select
                        options={secondaryDropdownOptions}
                        onSelectOption={onSelectSecondaryDropdownOption}
                        selectedOptionID={selectedSecondaryDropdownOptionID}
                        className="relative z-20 max-w-[220px]"
                        size="small"
                    />
                </div>
            )}

            {amount !== undefined && (
                <CurrencyField
                    type="number"
                    value={amount}
                    onChange={(newValue) => onAmountChange?.(newValue ?? 0)}
                    size="small"
                    step={0.01}
                    min={0}
                    className="relative z-20 max-w-[130px] shrink-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    onClick={(event) => event.stopPropagation()}
                />
            )}

            {quantity !== undefined && (
                <div
                    className="flex shrink-0 items-center justify-start gap-x-2"
                    onClick={(event) => event.stopPropagation()}
                >
                    <Label size="sm">Qty</Label>
                    <TextField
                        type="number"
                        value={quantity}
                        onChange={(event) => onQuantityChange?.(parseInt(event.target.value))}
                        size="small"
                        min={0}
                        className="relative z-20 max-w-[64px]"
                    />
                </div>
            )}

            {(secondaryTitle !== undefined || secondarySubtitle !== undefined) && (
                <div className="flex w-[80px] min-w-0 shrink-0 flex-col items-end gap-y-1">
                    {secondaryTitle !== undefined && <Label size="sm">{secondaryTitle}</Label>}
                    {secondarySubtitle !== undefined && (
                        <Label size="sm" className="font-light text-gray-600">
                            {secondarySubtitle}
                        </Label>
                    )}
                </div>
            )}

            {primaryButtonTitle !== undefined && (
                <Button
                    intent="tertiaryLink"
                    onClick={onClickPrimaryButton}
                    className="text-label-sm text-primary-700 hover:text-primary-700/60 h-8 px-1"
                    size="small"
                >
                    {primaryButtonTitle}
                </Button>
            )}
        </div>
    )
}
